// src/pages/dashboards/SupervisorDashboard.jsx
//
// Após o redesign v2 (mai/26), este componente ficou bem fino: ele só
// faz o gate de assinatura (ACTIVE/TRIALING) e, se passar, renderiza
// o AgentDash — que já contém TUDO (TopBar com botões, modais de criação
// de agente/chip, KPIs, Ranking, etc).
//
// Antes, SupervisorDash tinha:
//   - Botões flutuantes no canto inferior direito ("Adicionar Agente",
//     "Conectar Chip Oficial") → migraram pra TopBar do AgentDash.
//   - Modais de criação → também migraram (AgentDash agora os controla).
//
// Manter o componente em vez de remover de vez evita mexer nas rotas e
// no AppRouter (várias rotas apontam pra SupervisorDash).
import { useEffect, useState } from 'react';
import { Box, Typography, Button, CircularProgress, Stack, Chip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AgentDash from './AgentDash';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.cettosystem.com.br';

export default function SupervisorDashboard() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  // Estado de controle de assinatura.
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loadingSubscription, setLoadingSubscription] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  // Verifica status da assinatura ao montar.
  useEffect(() => {
    const checkSubscription = async () => {
      if (!token) { navigate('/login'); return; }
      try {
        const res = await axios.get(`${API_URL}/billing/subscription-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSubscriptionStatus(res.data.subscription_status);
      } catch (err) {
        if (err.response?.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          navigate('/login');
        } else {
          // Em caso de erro de rede, assume INACTIVE por segurança
          setSubscriptionStatus('INACTIVE');
        }
      } finally {
        setLoadingSubscription(false);
      }
    };
    checkSubscription();
  }, [token, navigate]);

  // Out/26: este botão abria o checkout direto, SEM os 7 dias grátis (só o
  // cadastro pedia o teste) — quem fechou a página do Stripe e voltou pelo
  // login era cobrado na hora. Agora sem assinatura volta pro cadastro, no
  // passo que faltou, e o servidor decide o teste. Pagamento em atraso já tem
  // assinatura: abrir outro checkout criaria uma segunda — vai pro portal do
  // Stripe trocar o cartão.
  const handleContinuar = async () => {
    if (subscriptionStatus !== 'PAST_DUE') {
      navigate('/register?retomar=1');
      return;
    }
    setCheckoutLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}/billing/customer-portal`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = res.data.url;
    } catch (err) {
      alert('Erro ao abrir o pagamento: ' + (err.response?.data?.detail || 'Tente novamente.'));
      setCheckoutLoading(false);
    }
  };

  // Tela de carregamento enquanto verifica assinatura.
  if (loadingSubscription) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#0f172a', alignItems: 'center', justifyContent: 'center' }}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress sx={{ color: '#25d366' }} size={48} />
          <Typography color="rgba(255,255,255,0.6)" variant="body2">Verificando sua assinatura...</Typography>
        </Stack>
      </Box>
    );
  }

  // Tela de bloqueio para assinaturas inativas.
  const ACTIVE_STATUSES = ['ACTIVE', 'TRIALING'];
  if (!ACTIVE_STATUSES.includes(subscriptionStatus)) {
    // PENDING também é quem abriu o pagamento e não terminou (o status nasce
    // assim no checkout), não só quem pagou e espera a confirmação.
    const statusMessages = {
      PENDING:  { title: 'Pagamento em Processamento', desc: 'Se você já concluiu o pagamento, aguarde alguns minutos. Se não terminou, continue o cadastro de onde parou.', botao: 'Continuar cadastro' },
      PAST_DUE: { title: 'Pagamento em Atraso', desc: 'Identificamos um problema com o pagamento da sua assinatura. Atualize seu método de pagamento para continuar.', botao: '💳 Atualizar forma de pagamento' },
      CANCELED: { title: 'Assinatura Cancelada', desc: 'Sua assinatura foi cancelada. Para reativar o acesso, assine um novo plano.', botao: 'Assinar novamente' },
      INACTIVE: { title: 'Assinatura Inativa', desc: 'Você ainda não possui uma assinatura ativa. Termine o cadastro para liberar o acesso: a primeira assinatura tem 7 dias grátis.', botao: 'Continuar cadastro' },
    };
    const msg = statusMessages[subscriptionStatus] || statusMessages['INACTIVE'];

    return (
      <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#0f172a', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Box sx={{ maxWidth: 480, width: '100%', textAlign: 'center' }}>
          <Box sx={{ width: 72, height: 72, bgcolor: 'rgba(37,211,102,0.1)', border: '2px solid #25d366', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3, fontSize: '2rem' }}>
            🔒
          </Box>
          <Chip
            label={subscriptionStatus || 'INATIVO'}
            size="small"
            sx={{ mb: 2, bgcolor: 'rgba(239,68,68,0.15)', color: '#f87171', border: '1px solid rgba(239,68,68,0.3)', fontWeight: 700, letterSpacing: 1 }}
          />
          <Typography variant="h5" fontWeight={700} color="white" mb={1}>{msg.title}</Typography>
          <Typography variant="body2" color="rgba(255,255,255,0.55)" mb={4} lineHeight={1.7}>{msg.desc}</Typography>
          <Stack spacing={2}>
            <Button
              variant="contained" fullWidth size="large"
              onClick={handleContinuar}
              disabled={checkoutLoading}
              sx={{ bgcolor: '#25d366', py: 1.8, fontWeight: 700, textTransform: 'none', fontSize: '1rem', borderRadius: 2, '&:hover': { bgcolor: '#1db954' } }}
            >
              {checkoutLoading
                ? <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={20} color="inherit" /><span>Redirecionando...</span></Stack>
                : msg.botao
              }
            </Button>
            <Button
              variant="text" fullWidth
              onClick={() => { localStorage.removeItem('token'); localStorage.removeItem('user'); navigate('/login'); }}
              sx={{ color: 'rgba(255,255,255,0.35)', textTransform: 'none', fontSize: '0.85rem' }}
            >
              Sair e usar outra conta
            </Button>
          </Stack>
          <Typography variant="caption" color="rgba(255,255,255,0.2)" display="block" mt={4}>
            Dúvidas? Entre em contato: contato@cettosystem.com.br
          </Typography>
        </Box>
      </Box>
    );
  }

  // Assinatura OK → renderiza o AgentDash, que já tem TopBar + modais.
  return <AgentDash />;
}
